import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { mattersApi, matterKeys } from '../api';
import type {
  MatterFilters,
  MatterResult,
  MatterPhase,
  CreateQuickMatterDto,
  SetComplianceDto,
  RiskLevel,
  CloseSuspendedDto,
  CreateInteractionDto,
  UpdateInteractionDto,
  CreateCourtInstanceDto,
  UpdateCourtInstanceDto,
  StartCaseInstanceDto,
  StopCaseInstanceDto,
  UpdateCaseInstanceDto,
  CreateDocumentDto,
  UpdateDocumentDto,
  BreakGlassRequestDto,
  GrantBreakGlassDto,
  CreateChangeRequestDto,
  UpdateMatterPlanDto,
} from '../types';

// Hook to fetch matters list
export function useMatters(filters: MatterFilters) {
  return useQuery({
    queryKey: matterKeys.list(filters),
    queryFn: () => mattersApi.getMatters(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch single matter
export function useMatter(id: string | undefined) {
  return useQuery({
    queryKey: matterKeys.detail(id ?? ''),
    queryFn: () => mattersApi.getMatter(id!),
    enabled: !!id,
    retry: 1,
  });
}

// Hook for matter mutations (create, update, delete)
export function useMatterMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const createMutation = useMutation({
    mutationFn: mattersApi.createMatter,
    onSuccess: () => {
      toast.success(t('matterCreatedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('errorCreatingMatter'));
      if (message?.includes('Contract already has a matter')) {
        toast.error(t('contractAlreadyHasMatter'));
      } else {
        toast.error(message);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: mattersApi.updateMatter,
    onSuccess: (_, variables) => {
      toast.success(t('matterUpdatedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: matterKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('errorUpdatingMatter'));
      if (message?.includes('Contract already has a matter')) {
        toast.error(t('contractAlreadyHasMatter'));
      } else {
        toast.error(message);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: mattersApi.deleteMatter,
    onSuccess: () => {
      toast.success(t('matterDeletedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingMatter')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}

// Hook for matter status mutations (close only — the legacy "start progress"
// flow was removed; the phase FSM is the only way a matter advances).
export function useMatterStatusMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const closeMutation = useMutation({
    mutationFn: ({
      id,
      result,
      softOverrideReason,
    }: {
      id: string | number;
      result: MatterResult;
      softOverrideReason?: string;
    }) => mattersApi.closeMatter(id, result, softOverrideReason),
    onSuccess: (_, variables) => {
      toast.success(t('matterClosedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: matterKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorClosingMatter')));
    },
  });

  return {
    closeMutation,
  };
}

// Hook for submitting NPS rating on a closed matter (K2)
export function useNpsMutation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const npsMutation = useMutation({
    mutationFn: ({ id, nps }: { id: string | number; nps: number }) =>
      mattersApi.submitNps(id, nps),
    onSuccess: (_, variables) => {
      toast.success(t('nps.submittedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: matterKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('nps.errorSubmitting')));
    },
  });

  return { npsMutation };
}

// Case 360° — fetch the Quality Gate check for a forward transition.
export function useGateCheck(
  id: string | number | undefined,
  toPhase: MatterPhase | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: matterKeys.gateCheck(id ?? '', toPhase),
    queryFn: () => mattersApi.gateCheck(id!, toPhase),
    enabled: !!id && enabled,
    staleTime: 0, // always re-check when the dialog opens
  });
}

// Case 360° (Stage 2) — current Lead + pending delegation.
export function useLead(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.lead(id ?? ''),
    queryFn: () => mattersApi.getLead(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 2) — Lead delegate / acknowledge / force-assign.
export function useLeadMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (id: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.lead(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.team(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.detail(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
  };

  const delegateMutation = useMutation({
    mutationFn: ({ id, toUserId }: { id: string | number; toUserId: number }) =>
      mattersApi.delegateLead(id, toUserId),
    onSuccess: (_, v) => {
      toast.success(t('leadDelegation.delegateSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) => toast.error(getApiErrorMessage(e, t('leadDelegation.delegateError'))),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => mattersApi.acknowledgeLead(id),
    onSuccess: (_, v) => {
      toast.success(t('leadDelegation.acknowledgeSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) => toast.error(getApiErrorMessage(e, t('leadDelegation.acknowledgeError'))),
  });

  const forceAssignMutation = useMutation({
    mutationFn: ({ id, toUserId }: { id: string | number; toUserId: number }) =>
      mattersApi.forceAssignLead(id, toUserId),
    onSuccess: (_, v) => {
      toast.success(t('leadDelegation.forceAssignSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) => toast.error(getApiErrorMessage(e, t('leadDelegation.forceAssignError'))),
  });

  return { delegateMutation, acknowledgeMutation, forceAssignMutation };
}

// Case 360° (Stage 14) — the Team tab read model.
export function useMatterTeam(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.team(id ?? ''),
    queryFn: () => mattersApi.getTeam(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 14) — inline team management (reassign partner, add/remove staff).
export function useTeamMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (id: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.detail(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.team(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.lead(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
  };

  const reassignPartnerMutation = useMutation({
    mutationFn: ({
      id,
      fromUserId,
      toUserId,
    }: {
      id: string | number;
      fromUserId?: number | undefined;
      toUserId: number;
    }) => mattersApi.reassignPartner(id, { fromUserId, toUserId }),
    onSuccess: (_, v) => {
      toast.success(t('matterTeam.reassignPartnerSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('matterTeam.reassignPartnerError'))),
  });

  const addStaffMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string | number; userId: number }) =>
      mattersApi.addStaff(id, userId),
    onSuccess: (_, v) => {
      toast.success(t('matterTeam.addSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('matterTeam.addError'))),
  });

  const removeStaffMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string | number; userId: number }) =>
      mattersApi.removeStaff(id, userId),
    onSuccess: (_, v) => {
      toast.success(t('matterTeam.removeSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('matterTeam.removeError'))),
  });

  const reassignStaffMutation = useMutation({
    mutationFn: ({
      id,
      fromUserId,
      toUserId,
    }: {
      id: string | number;
      fromUserId: number;
      toUserId: number;
    }) => mattersApi.reassignStaff(id, fromUserId, toUserId),
    onSuccess: (result, v) => {
      toast.success(
        t('matterTeam.reassignStaffSuccess', {
          count: result?.reassignedTasks ?? 0,
        }),
      );
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('matterTeam.reassignStaffError'))),
  });

  return {
    reassignPartnerMutation,
    addStaffMutation,
    removeStaffMutation,
    reassignStaffMutation,
  };
}

// Case 360° (Stage 14) — Matter Report export (PDF/DOCX, saved as a download).
export function useReportDownload() {
  const { t } = useTranslation();

  const downloadReportMutation = useMutation({
    mutationFn: ({
      id,
      format,
    }: {
      id: string | number;
      format: 'pdf' | 'docx';
    }) => mattersApi.downloadReport(id, format),
    onSuccess: (blob, v) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `matter-report-${v.id}.${v.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('report.success'));
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('report.error'))),
  });

  return { downloadReportMutation };
}

// Case 360° — move a matter forward through its Quality Gate.
export function useTransitionMutation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const transitionMutation = useMutation({
    mutationFn: ({
      id,
      toPhase,
      softOverrideReason,
    }: {
      id: string | number;
      toPhase: MatterPhase;
      softOverrideReason?: string;
    }) => mattersApi.transition(id, toPhase, softOverrideReason),
    onSuccess: (_, variables) => {
      toast.success(t('gate.transitionSuccess'));
      void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: matterKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('gate.transitionError')));
    },
  });

  return { transitionMutation };
}

// Case 360° (Stage 4) — Quick Advice: create / extend / convert.
export function useQuickMatterMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (id?: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
    if (id !== undefined) {
      void queryClient.invalidateQueries({ queryKey: matterKeys.detail(id) });
    }
  };

  const createQuickMutation = useMutation({
    mutationFn: (data: CreateQuickMatterDto) => mattersApi.createQuick(data),
    onSuccess: () => {
      toast.success(t('quickAdvice.createSuccess'));
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('quickAdvice.createError')));
    },
  });

  const extendQuickMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => mattersApi.extendQuick(id),
    onSuccess: (_, v) => {
      toast.success(t('quickAdvice.extendSuccess'));
      invalidate(v.id);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('quickAdvice.extendError')));
    },
  });

  const convertQuickMutation = useMutation({
    mutationFn: ({ id, note }: { id: string | number; note?: string }) =>
      mattersApi.convertQuick(id, note),
    onSuccess: (_, v) => {
      toast.success(t('quickAdvice.convertSuccess'));
      invalidate(v.id);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('quickAdvice.convertError')));
    },
  });

  return { createQuickMutation, extendQuickMutation, convertQuickMutation };
}

// Case 360° (Stage 6) — Compliance decision + Risk Level for a matter.
export function useCompliance(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.compliance(id ?? ''),
    queryFn: () => mattersApi.getCompliance(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 6) — firm-wide BYPASS rate (rolling 30 days) + MD override.
export function useBypassRate(enabled = true) {
  return useQuery({
    queryKey: matterKeys.bypassRate(),
    queryFn: () => mattersApi.getBypassRate(),
    enabled,
    staleTime: 60 * 1000,
  });
}

// Case 360° (Stage 6) — set Compliance / Risk Level / lift the BYPASS block.
export function useComplianceMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (id: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.compliance(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.detail(id) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.bypassRate() });
    void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
  };

  const setComplianceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: SetComplianceDto }) =>
      mattersApi.setCompliance(id, data),
    onSuccess: (_, v) => {
      toast.success(t('compliance.saveSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('compliance.saveError'))),
  });

  const setRiskLevelMutation = useMutation({
    mutationFn: ({ id, riskLevel }: { id: string | number; riskLevel: RiskLevel }) =>
      mattersApi.setRiskLevel(id, { riskLevel }),
    onSuccess: (_, v) => {
      toast.success(t('compliance.riskSaveSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('compliance.saveError'))),
  });

  const setBypassOverrideMutation = useMutation({
    mutationFn: ({ mdOverride }: { mdOverride: boolean }) =>
      mattersApi.setBypassOverride(mdOverride),
    onSuccess: () => {
      toast.success(t('compliance.overrideSuccess'));
      // The override is firm-wide and feeds every matter's bypass context, so
      // refresh all matter queries (bypass-rate + per-matter compliance state).
      void queryClient.invalidateQueries({ queryKey: matterKeys.all });
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('compliance.overrideError'))),
  });

  return {
    setComplianceMutation,
    setRiskLevelMutation,
    setBypassOverrideMutation,
  };
}

// Case 360° (Stage 5) — Emergency Mode: open / extend / resolve / archive.
export function useEmergencyMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (id: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: matterKeys.detail(id) });
  };

  const emergencyOpenMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => mattersApi.emergencyOpen(id),
    onSuccess: (_, v) => {
      toast.success(t('emergency.openSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('emergency.openError'))),
  });

  const emergencyExtendMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => mattersApi.emergencyExtend(id),
    onSuccess: (_, v) => {
      toast.success(t('emergency.extendSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('emergency.extendError'))),
  });

  const emergencyResolveMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => mattersApi.emergencyResolve(id),
    onSuccess: (_, v) => {
      toast.success(t('emergency.resolveSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('emergency.resolveError'))),
  });

  const closeSuspendedMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: CloseSuspendedDto }) =>
      mattersApi.closeSuspended(id, data),
    onSuccess: (_, v) => {
      toast.success(t('emergency.archiveSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('emergency.archiveError'))),
  });

  return {
    emergencyOpenMutation,
    emergencyExtendMutation,
    emergencyResolveMutation,
    closeSuspendedMutation,
  };
}

// Case 360° (Stage 7) — Interaction Log entries for a matter.
export function useInteractions(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.interactions(id ?? ''),
    queryFn: () => mattersApi.getInteractions(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 7) — log / fill interaction entries.
export function useInteractionMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (matterId: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.interactions(matterId) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.unfilledInteractions() });
    // Gate 3 reads the log — refresh any open gate-check.
    void queryClient.invalidateQueries({ queryKey: [...matterKeys.all, 'gate-check'] });
  };

  const createInteractionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: CreateInteractionDto }) =>
      mattersApi.createInteraction(id, data),
    onSuccess: (_, v) => {
      toast.success(t('interaction.createSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('interaction.createError'))),
  });

  const updateInteractionMutation = useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: number;
      matterId: string | number;
      data: UpdateInteractionDto;
    }) => mattersApi.updateInteraction(entryId, data),
    onSuccess: (_, v) => {
      toast.success(t('interaction.updateSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('interaction.updateError'))),
  });

  return { createInteractionMutation, updateInteractionMutation };
}

// Case 360° (Stage 9) — court instances for a court-case matter.
export function useCourtInstances(id: string | number | undefined, enabled = true) {
  return useQuery({
    queryKey: matterKeys.instances(id ?? ''),
    queryFn: () => mattersApi.getCourtInstances(id!),
    enabled: !!id && enabled,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 9) — open / update court instances.
export function useCourtInstanceMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (matterId: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.instances(matterId) });
  };

  const createInstanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: CreateCourtInstanceDto }) =>
      mattersApi.createCourtInstance(id, data),
    onSuccess: (_, v) => {
      toast.success(t('instance.createSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('instance.createError'))),
  });

  const updateInstanceMutation = useMutation({
    mutationFn: ({
      instanceId,
      data,
    }: {
      instanceId: number;
      matterId: string | number;
      data: UpdateCourtInstanceDto;
    }) => mattersApi.updateCourtInstance(instanceId, data),
    onSuccess: (_, v) => {
      toast.success(t('instance.updateSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('instance.updateError'))),
  });

  return { createInstanceMutation, updateInstanceMutation };
}

// Case Instances — engagements on an INSTANCE-billed matter.
export function useCaseInstances(id: string | number | undefined, enabled = true) {
  return useQuery({
    queryKey: matterKeys.caseInstances(id ?? ''),
    queryFn: () => mattersApi.getCaseInstances(id!),
    enabled: !!id && enabled,
    staleTime: 30 * 1000,
  });
}

// Case Instances — start / stop / edit.
export function useCaseInstanceMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (matterId: string | number) => {
    void queryClient.invalidateQueries({
      queryKey: matterKeys.caseInstances(matterId),
    });
  };

  const startCaseInstanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: StartCaseInstanceDto }) =>
      mattersApi.startCaseInstance(id, data),
    onSuccess: (r) => {
      toast.success(t('caseInstance.startSuccess'));
      invalidate(r.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('caseInstance.startError'))),
  });

  const stopCaseInstanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: StopCaseInstanceDto }) =>
      mattersApi.stopCaseInstance(id, data),
    onSuccess: (r) => {
      toast.success(t('caseInstance.stopSuccess'));
      invalidate(r.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('caseInstance.stopError'))),
  });

  const updateCaseInstanceMutation = useMutation({
    mutationFn: ({
      id,
      instanceId,
      data,
    }: {
      id: string | number;
      instanceId: number;
      data: UpdateCaseInstanceDto;
    }) => mattersApi.updateCaseInstance(id, instanceId, data),
    onSuccess: (r) => {
      toast.success(t('caseInstance.updateSuccess'));
      invalidate(r.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('caseInstance.updateError'))),
  });

  return {
    startCaseInstanceMutation,
    stopCaseInstanceMutation,
    updateCaseInstanceMutation,
  };
}

// Case 360° (Stage 10) — documents of a matter (SECRET rows may come back locked).
export function useMatterDocuments(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.documents(id ?? ''),
    queryFn: () => mattersApi.getDocuments(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 10) — full document detail with the version history.
export function useMatterDocument(documentId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: matterKeys.document(documentId ?? 0),
    queryFn: () => mattersApi.getDocument(documentId!),
    enabled: !!documentId && enabled,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 10) — create / version / rollback / SECRET / break-glass.
export function useDocumentMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (matterId: string | number, documentId?: number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.documents(matterId) });
    if (documentId) {
      void queryClient.invalidateQueries({ queryKey: matterKeys.document(documentId) });
    }
  };

  const createDocumentMutation = useMutation({
    mutationFn: (data: CreateDocumentDto) => mattersApi.createDocument(data),
    onSuccess: (_, v) => {
      toast.success(t('document.createSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.createError'))),
  });

  const addVersionMutation = useMutation({
    mutationFn: ({
      documentId,
      file,
    }: {
      documentId: number;
      matterId: string | number;
      file: File;
    }) => mattersApi.addDocumentVersion(documentId, file),
    onSuccess: (_, v) => {
      toast.success(t('document.versionSuccess'));
      invalidate(v.matterId, v.documentId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.versionError'))),
  });

  const updateDocumentMutation = useMutation({
    mutationFn: ({
      documentId,
      data,
    }: {
      documentId: number;
      matterId: string | number;
      data: UpdateDocumentDto;
    }) => mattersApi.updateDocument(documentId, data),
    onSuccess: (_, v) => {
      toast.success(t('document.updateSuccess'));
      invalidate(v.matterId, v.documentId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.updateError'))),
  });

  const rollbackDocumentMutation = useMutation({
    mutationFn: ({
      documentId,
      versionId,
    }: {
      documentId: number;
      matterId: string | number;
      versionId: number;
    }) => mattersApi.rollbackDocument(documentId, versionId),
    onSuccess: (_, v) => {
      toast.success(t('document.rollbackSuccess'));
      invalidate(v.matterId, v.documentId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.rollbackError'))),
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: ({
      documentId,
    }: {
      documentId: number;
      matterId: string | number;
    }) => mattersApi.deleteDocument(documentId),
    onSuccess: (_, v) => {
      toast.success(t('document.deleteSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.deleteError'))),
  });

  // Download a version's file (streamed from MinIO through the authorized
  // API) and save it under the original file name.
  const downloadVersionMutation = useMutation({
    mutationFn: ({
      documentId,
      versionId,
    }: {
      documentId: number;
      versionId: number;
      fileName: string;
    }) => mattersApi.downloadDocumentVersion(documentId, versionId),
    onSuccess: (blob, v) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = v.fileName || `document-${v.documentId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.downloadError'))),
  });

  const requestBreakGlassMutation = useMutation({
    mutationFn: ({
      documentId,
      data,
    }: {
      documentId: number;
      matterId: string | number;
      data: BreakGlassRequestDto;
    }) => mattersApi.requestBreakGlass(documentId, data),
    onSuccess: () => {
      toast.success(t('document.breakGlassRequested'));
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.breakGlassRequestError'))),
  });

  const grantBreakGlassMutation = useMutation({
    mutationFn: ({
      documentId,
      data,
    }: {
      documentId: number;
      matterId: string | number;
      data: GrantBreakGlassDto;
    }) => mattersApi.grantBreakGlass(documentId, data),
    onSuccess: (_, v) => {
      toast.success(t('document.breakGlassGranted'));
      invalidate(v.matterId, v.documentId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.breakGlassGrantError'))),
  });

  const denyBreakGlassMutation = useMutation({
    mutationFn: ({
      documentId,
      data,
    }: {
      documentId: number;
      matterId: string | number;
      data: { auditorId: number };
    }) => mattersApi.denyBreakGlass(documentId, data),
    onSuccess: (_, v) => {
      toast.success(t('document.breakGlassDenied'));
      invalidate(v.matterId, v.documentId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('document.breakGlassDenyError'))),
  });

  return {
    createDocumentMutation,
    addVersionMutation,
    updateDocumentMutation,
    rollbackDocumentMutation,
    deleteDocumentMutation,
    downloadVersionMutation,
    requestBreakGlassMutation,
    grantBreakGlassMutation,
    denyBreakGlassMutation,
  };
}

// Case 360° (Stage 11) — change requests of a matter (newest first).
export function useMatterChangeRequests(id: string | number | undefined) {
  return useQuery({
    queryKey: matterKeys.changeRequests(id ?? ''),
    queryFn: () => mattersApi.getChangeRequests(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// Case 360° (Stage 11) — Matter Plan edits + CR create/approve/reject.
export function useChangeRequestMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = (matterId: string | number) => {
    void queryClient.invalidateQueries({ queryKey: matterKeys.changeRequests(matterId) });
    void queryClient.invalidateQueries({ queryKey: matterKeys.detail(String(matterId)) });
  };

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateMatterPlanDto }) =>
      mattersApi.updatePlan(id, data),
    onSuccess: (_, v) => {
      toast.success(t('plan.updateSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('plan.updateError'))),
  });

  const confirmPlanMutation = useMutation({
    mutationFn: (id: string | number) => mattersApi.confirmPlan(id),
    onSuccess: (_, id) => {
      toast.success(t('plan.confirmSuccess'));
      invalidate(id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('plan.confirmError'))),
  });

  const requestFeedbackMutation = useMutation({
    mutationFn: (id: string | number) => mattersApi.requestFeedback(id),
    onSuccess: (_, id) => {
      toast.success(t('plan.feedbackSuccess'));
      invalidate(id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('plan.feedbackError'))),
  });

  const createCrMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: CreateChangeRequestDto }) =>
      mattersApi.createChangeRequest(id, data),
    onSuccess: (_, v) => {
      toast.success(t('cr.createSuccess'));
      invalidate(v.id);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('cr.createError'))),
  });

  const approveCrMutation = useMutation({
    mutationFn: ({ crId }: { crId: number; matterId: string | number }) =>
      mattersApi.approveChangeRequest(crId),
    onSuccess: (_, v) => {
      toast.success(t('cr.approveSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('cr.approveError'))),
  });

  const rejectCrMutation = useMutation({
    mutationFn: ({
      crId,
      rejectReason,
    }: {
      crId: number;
      matterId: string | number;
      rejectReason: string;
    }) => mattersApi.rejectChangeRequest(crId, rejectReason),
    onSuccess: (_, v) => {
      toast.success(t('cr.rejectSuccess'));
      invalidate(v.matterId);
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('cr.rejectError'))),
  });

  return {
    updatePlanMutation,
    confirmPlanMutation,
    requestFeedbackMutation,
    createCrMutation,
    approveCrMutation,
    rejectCrMutation,
  };
}
